import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import './Sidebar.css';

import logo from './assets/moonx24-logo.png';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [openSubMenu, setOpenSubMenu] = useState(null);

  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.remove('sidebar-open');
      document.body.style.overflow = '';
    } else {
      document.body.classList.add('sidebar-open');
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.classList.remove('sidebar-open');
      document.body.style.overflow = '';
    };
  }, [isCollapsed]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsCollapsed(true);
        setOpenSubMenu(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const rol = user?.rol;

  const menuItems = useMemo(() => {
    const servisFormuGorebilir =
      rol === 'admin' || rol === 'teknisyen';

    return [
      { path: '/dashboard', icon: '📊', label: 'Dashboard' },
      { path: '/kompresor-detay', icon: '🗃️', label: 'Kompresör Detay' },
      { path: '/kompresor-kayitlari', icon: '📁', label: 'Kompresör Kayıtları' },
      { path: '/kompresor-hesaplayici', icon: '⚙️', label: 'Sistem Seçimi' },
      { path: '/bakim-planlayici', icon: '🛠️', label: 'Bakım Planlayıcı' },
      ...(servisFormuGorebilir
        ? [{ path: '/servis-form', icon: '📝', label: 'Servis Formu' }]
        : []),
      { path: '/tedarikci-yonetimi', icon: '🏷️', label: 'Tedarikçi Yönetimi' },
      { path: '/muadil-yonetim', icon: '🔁', label: 'Muadil Yönetim' },
      { path: '/teklif-gonder', icon: '📤', label: 'Teklif Gönder' },
      { path: '/stok-takip', icon: '📦', label: 'Stok Takip' },
      { path: '/parca-esleme', icon: '🤖', label: 'AI Parça Eşleme' },
      { path: '/eposta-teklif', icon: '✉️', label: 'E-posta Teklif Gönder' },
      { path: '/compressor-yonetimi', icon: '📌', label: 'Kompresör Parça Takibi' },
      {
        id: 'admin',
        icon: '🛠️',
        label: 'Admin Panel',
        role: 'admin',
        submenus: [
          { path: '/admin-panel', label: 'Genel Ayarlar' },
          { path: '/kullanici-yonetimi', label: 'Kullanıcı Yönetimi' }
        ]
      }
    ];
  }, [rol]);

  const filteredMenuItems = menuItems.filter(
    (item) => !item.role || item.role === rol
  );

  const isActive = (path) =>
    location.pathname === path ||
    location.pathname.startsWith(`${path}/`);

  const handleNavigate = (path) => {
    navigate(path);
    setIsCollapsed(true);
    setOpenSubMenu(null);
  };

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsCollapsed(true);
    setOpenSubMenu(null);
  };

  if (!user) return null;

  return (
    <>
      {isCollapsed && (
        <button
          type="button"
          className="sidebar-toggle"
          onClick={toggleSidebar}
          aria-label="Menüyü Aç"
        >
          ☰
        </button>
      )}

      <div
        className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}
        aria-hidden={isCollapsed}
      >
        <div className="sidebar-header">
          <div className="logo">
            <img
              src={logo}
              alt=""
              className="sidebar-logo"
            />
            <span className="logo-text">MOON X24</span>
          </div>

          {!isCollapsed && (
            <button
              type="button"
              className="close-btn"
              onClick={toggleSidebar}
              aria-label="Menüyü Kapat"
            >
              ×
            </button>
          )}
        </div>

        <div className="sidebar-body">
          <div className="user-section">
            <div className="avatar">👤</div>

            <div className="user-info">
              <span className="user-name">{user.ad}</span>
              <span className="user-role">{rol}</span>
            </div>
          </div>

          <nav className="menu">
            {filteredMenuItems.map((item) =>
              item.submenus ? (
                <div
                  key={item.id}
                  className="menu-item submenu"
                >
                  <button
                    type="button"
                    className={`menu-link has-submenu ${
                      openSubMenu === item.id ? 'open' : ''
                    }`}
                    onClick={() =>
                      setOpenSubMenu(
                        openSubMenu === item.id ? null : item.id
                      )
                    }
                    aria-expanded={
                      openSubMenu === item.id
                    }
                  >
                    <span className="icon">{item.icon}</span>
                    <span className="label">{item.label}</span>
                    <span className="arrow">
                      {openSubMenu === item.id ? '▲' : '▶'}
                    </span>
                  </button>

                  {openSubMenu === item.id && (
                    <div className="submenu-items">
                      {item.submenus.map((sub) => (
                        <button
                          key={sub.path}
                          type="button"
                          className={`submenu-link ${
                            isActive(sub.path) ? 'active' : ''
                          }`}
                          onClick={() =>
                            handleNavigate(sub.path)
                          }
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={item.path}
                  type="button"
                  className={`menu-item ${
                    isActive(item.path) ? 'active' : ''
                  }`}
                  onClick={() => handleNavigate(item.path)}
                >
                  <span className="icon">{item.icon}</span>
                  <span className="label">{item.label}</span>
                </button>
              )
            )}
          </nav>
        </div>

        <div className="logout-section">
          <button
            type="button"
            onClick={handleLogout}
            className="logout-btn"
          >
            🚪 Oturumu Kapat
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div
          className="sidebar-overlay active"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Sidebar;